import { Box, Button, Divider, Typography } from "@mui/material";
import {
  FunctionCall,
  TodoItem,
  PlanDetail,
} from "components/hooks/useTaskAgentState";
import { ActionDisplay } from "./actions/ActionDisplay";
import {
  PlanDetailsSection,
  PlanDetailsSectionRef,
} from "./PlanDetailsSection";
import { Family } from "protogen/advisors_service_pb";
import { useRef, useState } from "react";
import { Pencil } from "lucide-react";
import LinkRouter from "components/navigation/LinkRouter";

const TodoEntry = ({ todo }: { todo: TodoItem }) => {
  return (
    <Box display="flex" flexDirection="row" alignItems="center" gap="12px">
      <Box flex="1">
        <Typography
          variant="body"
          color={
            todo.status === "in_progress" ? "text.primary" : "text.secondary"
          }
        >
          {todo.content}
          {todo.status === "in_progress" ? "..." : ""}
        </Typography>
      </Box>
    </Box>
  );
};

const PlaybookEntry = ({
  todo,
  playbookRef,
}: {
  todo: TodoItem;
  playbookRef: string;
}) => {
  const playbookName = todo.step?.slice("running playbook: ".length).trim();
  if (!playbookName) {
    return null;
  }
  return (
    <Box display="flex" flexDirection="row" alignItems="center" gap="12px">
      <Box flex="1">
        <Typography variant="body" color={"text.secondary"}>
          Running playbook:{" "}
          <LinkRouter
            inline={true}
            to={`/tools/agents/playbooks/${encodeURIComponent(playbookRef)}`}
            useColorScheme
          >
            {playbookName}
          </LinkRouter>
        </Typography>
      </Box>
    </Box>
  );
};

const PlanStep = ({
  item,
  accountType,
  playbookRef,
  hideDividers,
}: {
  item: {
    plan: TodoItem;
    actions: FunctionCall[];
  };
  accountType: "advisor" | "member";
  playbookRef?: string;
  hideDividers?: boolean;
}) => {
  const isPlaybook = item.plan.step
    ?.toLowerCase()
    .startsWith("running playbook: ");

  if (accountType === "member" && isPlaybook) {
    return null;
  }

  return (
    <Box>
      {isPlaybook && item.plan.status === "completed" && playbookRef ? (
        <PlaybookEntry todo={item.plan} playbookRef={playbookRef} />
      ) : (
        <TodoEntry todo={item.plan} />
      )}
      {/* Actions belonging to this plan */}
      {item.actions.length > 0 && (
        <Box
          sx={{
            marginTop: "8px",
            display: "flex",
            flexDirection: "column",
            gap: "0",
          }}
        >
          {item.actions.map((call) => (
            <Box sx={{}} key={call.id}>
              <ActionDisplay
                key={call.id}
                call={call}
                accountType={accountType}
              />
            </Box>
          ))}
        </Box>
      )}
      {!hideDividers && <Divider sx={{ marginTop: "8px" }} />}
    </Box>
  );
};

export const PlanWithActionsSection = ({
  planWithActions,
  unassociatedActions,
  onUpdatePlanDetails,
  accountType = "advisor",
  isActive = false,
  family,
  playbookRef,
  showActions,
  hideDividers = false,
}: {
  planWithActions: Array<{
    plan: TodoItem;
    actions: FunctionCall[];
  }>;
  unassociatedActions?: FunctionCall[];
  onUpdatePlanDetails?: (details: PlanDetail[]) => void;
  accountType?: "advisor" | "member";
  isActive?: boolean;
  family?: Family;
  playbookRef?: string;
  showActions?: boolean;
  hideDividers?: boolean;
}) => {
  // Filter to only show completed and in_progress items
  let visiblePlanWithActions = planWithActions.filter(
    (item) =>
      item.plan.status === "completed" || item.plan.status === "in_progress",
  );

  const hasPlans = visiblePlanWithActions.length > 0;
  const hasUnassociated = unassociatedActions && unassociatedActions.length > 0;

  // Get details from any plan item (details are shared across all plan items)
  const details =
    visiblePlanWithActions[0]?.plan.details || planWithActions[0]?.plan.details;

  const planDetailsSectionRef = useRef<PlanDetailsSectionRef>(null);
  const [isEditingDetails, setIsEditingDetails] = useState(false);

  if (!hasPlans && !hasUnassociated && (!details || details.length === 0))
    return null;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: hideDividers ? "12px" : "8px",
      }}
    >
      {/* Show plan details at the top if they exist */}
      {details && details.length > 0 && (
        <Box>
          <Box
            display="flex"
            flexDirection="row"
            alignItems="center"
            gap="12px"
          >
            <Box flex="1">
              <Typography variant="body" color="text.secondary">
                Collecting details for task
              </Typography>
            </Box>
            {onUpdatePlanDetails && !isEditingDetails && (
              <Button
                variant="text"
                size="small"
                startIcon={<Pencil size={14} />}
                onClick={() => planDetailsSectionRef.current?.openEdit()}
                sx={{ height: "unset", minWidth: "auto", padding: "2px 8px" }}
              >
                Update details
              </Button>
            )}
          </Box>
          <PlanDetailsSection
            ref={planDetailsSectionRef}
            details={details}
            onUpdateDetails={onUpdatePlanDetails}
            isActive={isActive}
            canRevealSecrets={accountType === "advisor"}
            family={family}
            onEditingChange={setIsEditingDetails}
          />
          {showActions && !hideDividers && (
            <Divider sx={{ marginTop: "8px" }} />
          )}
        </Box>
      )}
      {showActions && (
        <>
          {/* Show plans with their associated actions */}
          {visiblePlanWithActions.map((item, planIndex) => (
            <PlanStep
              key={item.plan.id || planIndex}
              item={item}
              accountType={accountType}
              playbookRef={playbookRef}
              hideDividers={hideDividers}
            />
          ))}
          {/* Show unassociated actions at the end if plans exist */}
          {hasUnassociated && (
            <Box>
              {!(hideDividers && hasPlans) && (
                <Typography
                  variant="bodySmall"
                  color="text.secondary"
                  sx={{ marginBottom: "8px" }}
                >
                  Additional Actions
                </Typography>
              )}
              {unassociatedActions.map((call) => (
                <ActionDisplay
                  key={call.id}
                  call={call}
                  accountType={accountType}
                />
              ))}
              {!hideDividers && <Divider sx={{ marginTop: "8px" }} />}
            </Box>
          )}
        </>
      )}
    </Box>
  );
};
